// Drag libre de una ventana flotante por su cabecera (pointer events, sin
// dependencias) — port del CRM (crm/apps/web/src/shared/callConsole/
// useDraggable.ts) a este SPA. La posición persiste SOLO durante la sesión
// (useRef/useState en memoria; el requisito pide no usar localStorage salvo
// trivial → aquí no hace falta que sobreviva a recargas).
//
// Devuelve `pos` (left/top en px) y los handlers a pinchar en el "handle".

import { useCallback, useRef, useState } from "react";

export interface Pos {
  x: number;
  y: number;
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(Math.max(v, min), max);
}

export function useDraggable(initial: Pos) {
  const [pos, setPos] = useState<Pos>(initial);

  // Espejo de la última posición para leerla en los handlers sin closure stale.
  const posRef = useRef(pos);
  posRef.current = pos;
  // Offset entre el cursor y la esquina al empezar a arrastrar.
  const grabRef = useRef<{ dx: number; dy: number } | null>(null);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    grabRef.current = {
      dx: e.clientX - posRef.current.x,
      dy: e.clientY - posRef.current.y,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const grab = grabRef.current;
    if (!grab) return;
    // Mantenemos siempre una franja visible (no se pierde fuera del viewport).
    const x = clamp(e.clientX - grab.dx, 0, window.innerWidth - 80);
    const y = clamp(e.clientY - grab.dy, 0, window.innerHeight - 40);
    setPos({ x, y });
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    if (!grabRef.current) return;
    grabRef.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
  }, []);

  return { pos, dragHandlers: { onPointerDown, onPointerMove, onPointerUp } };
}
